import type {
  SortDirection,
  TableColumn,
  TableRow,
} from '@data/components/basic/Table/types';

export type RowId = string | number;

export interface RowTree {
  /** The top-level rows, in the order the service sent them. */
  rows: TableRow[];
  /** Each row's children by its id, in the order the service sent them. */
  childRows: Map<RowId, TableRow[]>;
}

export interface RowTreeKeys {
  idKey?: string;
  parentKey?: string;
}

/**
 * Nests a flat, self-linked table — each row naming its parent by id — into
 * top-level rows and their children.
 *
 * A row is top-level when its parent is not itself a row: the service links
 * groups to a root it does not send, so "no parent" cannot be relied on.
 */
export function buildRowTree(
  columns: TableColumn[],
  rows: TableRow[],
  { idKey = 'NODE_ID', parentKey = 'PARENT_ID' }: RowTreeKeys = {}
): RowTree {
  const idIndex = columns.findIndex((col) => col.key === idKey);
  const parentIndex = columns.findIndex((col) => col.key === parentKey);
  if (idIndex < 0 || parentIndex < 0) {
    return { rows, childRows: new Map() };
  }

  const ids = new Set(rows.map((row) => row[idIndex]));
  const topRows: TableRow[] = [];
  const childRows = new Map<RowId, TableRow[]>();

  rows.forEach((row) => {
    const parentId = row[parentIndex];
    if (parentId == null || !ids.has(parentId)) {
      topRows.push(row);
      return;
    }
    const siblings = childRows.get(parentId) ?? [];
    siblings.push(row);
    childRows.set(parentId, siblings);
  });

  return { rows: topRows, childRows };
}

/**
 * Searches a tree's `key` column, ignoring case. A matching top-level row keeps
 * all its children; otherwise it stays only when some children match, and
 * keeps just those.
 */
export function filterRowTree(
  tree: RowTree,
  columns: TableColumn[],
  search: string,
  key: string,
  { idKey = 'NODE_ID' }: Pick<RowTreeKeys, 'idKey'> = {}
): RowTree {
  const term = search.trim().toLowerCase();
  const index = columns.findIndex((col) => col.key === key);
  const idIndex = columns.findIndex((col) => col.key === idKey);
  if (!term || index < 0) return tree;

  const matches = (row: TableRow) =>
    String(row[index] ?? '')
      .toLowerCase()
      .includes(term);
  const rows: TableRow[] = [];
  const childRows = new Map(tree.childRows);

  tree.rows.forEach((row) => {
    if (matches(row)) {
      rows.push(row);
      return;
    }
    const id = row[idIndex] as RowId;
    const children = (idIndex < 0 ? undefined : tree.childRows.get(id)) ?? [];
    const matching = children.filter(matches);
    if (matching.length) {
      rows.push(row);
      childRows.set(id, matching);
    }
  });

  return { rows, childRows };
}

/** Empties the given columns on every child row, e.g. values only a parent shows. */
export function clearChildColumns(
  tree: RowTree,
  columns: TableColumn[],
  keys: string[]
): RowTree {
  const indices = columns
    .map((col, index) => (keys.includes(col.key) ? index : -1))
    .filter((index) => index >= 0);
  if (!indices.length) return tree;

  const clear = (row: TableRow) =>
    row.map((value, index) => (indices.includes(index) ? null : value));
  return {
    rows: tree.rows,
    childRows: new Map(
      [...tree.childRows].map(([id, children]) => [id, children.map(clear)])
    ),
  };
}

export interface ParentColumn {
  key: string;
  label: string;
}

/**
 * Lays a tree back out flat — each row followed by all of its children — with
 * a column after `nameKey` naming each row's parent, empty on top-level rows.
 * For exports, where nesting cannot be shown.
 */
export function flattenRowTree(
  tree: RowTree,
  columns: TableColumn[],
  nameKey: string,
  parentColumn: ParentColumn,
  { idKey = 'NODE_ID' }: Pick<RowTreeKeys, 'idKey'> = {}
): { columns: TableColumn[]; rows: TableRow[] } {
  const idIndex = columns.findIndex((col) => col.key === idKey);
  const nameIndex = columns.findIndex((col) => col.key === nameKey);
  const insertAt = nameIndex < 0 ? columns.length : nameIndex + 1;

  const withParent = (row: TableRow, parentName: TableRow[number]) => [
    ...row.slice(0, insertAt),
    parentName,
    ...row.slice(insertAt),
  ];

  const rows: TableRow[] = [];
  const addRows = (list: TableRow[], parentName: TableRow[number]) => {
    list.forEach((row) => {
      rows.push(withParent(row, parentName));
      const children =
        idIndex >= 0 ? tree.childRows.get(row[idIndex] as RowId) : undefined;
      addRows(children ?? [], row[nameIndex] ?? null);
    });
  };
  addRows(tree.rows, null);

  return {
    columns: [
      ...columns.slice(0, insertAt),
      { type: 'string', label: parentColumn.label, key: parentColumn.key },
      ...columns.slice(insertAt),
    ],
    rows,
  };
}

export interface RowTreeSort {
  key: string;
  direction: SortDirection;
  /** Orders rows that tie on `key`, always A→Z. */
  tieBreakKey?: string;
}

type CellValue = TableRow[number];

function compareValues(a: CellValue, b: CellValue): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
}

/** Compares in the given direction, keeping empty values last either way. */
function compareSortValues(a: CellValue, b: CellValue, sign: number): number {
  if (a == null || b == null) {
    if (a == null && b == null) return 0;
    return a == null ? 1 : -1;
  }
  return compareValues(a, b) * sign;
}

/**
 * Sorts a tree in the browser, for tables fetched in one pass: the top-level
 * rows first, then each row's children among themselves, so children stay
 * under their parent. Empty values always go last.
 */
export function sortRowTree(
  tree: RowTree,
  columns: TableColumn[],
  { key, direction, tieBreakKey }: RowTreeSort
): RowTree {
  const index = columns.findIndex((col) => col.key === key);
  const tieIndex = columns.findIndex((col) => col.key === tieBreakKey);
  if (index < 0) return tree;

  const sign = direction === 'desc' ? -1 : 1;
  const compareRows = (a: TableRow, b: TableRow) =>
    compareSortValues(a[index], b[index], sign) ||
    (tieIndex < 0 ? 0 : compareValues(a[tieIndex], b[tieIndex]));
  const sortRows = (rows: TableRow[]) => [...rows].sort(compareRows);

  return {
    rows: sortRows(tree.rows),
    childRows: new Map(
      [...tree.childRows].map(([id, children]) => [id, sortRows(children)])
    ),
  };
}
